/**
 * A sale typed in by hand: a customer, Date Sold, and what was sold. The rules fill in the rest on
 * save (the name, the owner's tier, Expected Commission), so the form does not ask for them.
 * A sale from a lead is converted from the lead instead, which carries its figures over.
 */
import { CALL_TYPES, POLICY_STAGES, SALE_STAGES, type ContactSummary } from "@tlcrm/shared";
import { useState } from "react";
import { useNavigate } from "react-router";

import { useCreatePolicy, usePicklists, useUsers } from "../api/hooks";
import { useMe } from "../auth/session";
import { useToast } from "../components/Toasts";
import { FieldBox, Modal, Select } from "../components/ui";
import { ContactPicker } from "./ContactPicker";
import { useForm } from "./useForm";

const num = (s: string) => (s.trim() ? Number(s.replace(/[$,]/g, "")) : null);

export function NewPolicyModal({
  onClose,
  customer: given,
}: {
  onClose: () => void;
  customer?: ContactSummary;
}) {
  const { me, can } = useMe();
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreatePolicy();
  const picklists = usePicklists();
  const users = useUsers();
  const [customer, setCustomer] = useState<ContactSummary | null>(given ?? null);
  const f = useForm({
    dateSold: "",
    stage: "issued" as string,
    carrierId: "",
    productId: "",
    totalPremium: "",
    totalCoverage: "",
    effectiveDate: "",
    insuredDob: "",
    callType: "" as string,
    ownerId: me.user.id,
  });
  const v = f.values;

  const submit = async () => {
    try {
      const policy = await create.mutateAsync({
        customerId: customer?.id,
        dateSold: v.dateSold,
        stage: v.stage,
        carrierId: v.carrierId || null,
        productId: v.productId || null,
        totalPremium: num(v.totalPremium),
        totalCoverage: num(v.totalCoverage),
        effectiveDate: v.effectiveDate,
        ...(v.insuredDob && { insuredDob: v.insuredDob }),
        callType: v.callType || null,
        ownerId: v.ownerId,
      });
      toast(`${policy.name} is on the books.`);
      onClose();
      navigate(`/policies/${policy.id}`);
    } catch (err) {
      f.fail(err);
    }
  };

  return (
    <Modal
      title="New policy"
      onClose={onClose}
      width={620}
      footer={
        <>
          {f.formError && (
            <span className="left" style={{ color: "var(--brick)" }}>
              {f.formError}
            </span>
          )}
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => void submit()}
            disabled={create.isPending}
          >
            {create.isPending ? "Saving…" : "Save policy"}
          </button>
        </>
      }
    >
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FieldBox label="Customer" htmlFor="np-customer" error={f.errors.customerId} full>
          <ContactPicker
            id="np-customer"
            value={customer}
            onChange={setCustomer}
            invalid={Boolean(f.errors.customerId)}
          />
        </FieldBox>
        <FieldBox label="Date Sold" htmlFor="np-sold" error={f.errors.dateSold}>
          <input id="np-sold" placeholder="MM-DD-YYYY" {...f.text("dateSold", "mono")} />
        </FieldBox>
        <FieldBox label="Stage" htmlFor="np-stage">
          <Select
            id="np-stage"
            value={v.stage}
            onChange={(x) => f.set("stage")(x || "issued")}
            options={POLICY_STAGES.filter(([s]) => SALE_STAGES.includes(s))}
          />
        </FieldBox>
        <FieldBox label="Carrier Sold" htmlFor="np-carrier" error={f.errors.carrierId}>
          <Select
            id="np-carrier"
            value={v.carrierId}
            onChange={f.set("carrierId")}
            options={(picklists.data?.carriers ?? [])
              .filter((c) => c.active)
              .map((c) => [c.id, c.name] as const)}
            empty="Not chosen"
          />
        </FieldBox>
        <FieldBox label="Policy Type" htmlFor="np-product" error={f.errors.productId}>
          <Select
            id="np-product"
            value={v.productId}
            onChange={f.set("productId")}
            options={(picklists.data?.products ?? [])
              .filter((c) => c.active)
              .map((c) => [c.id, c.name] as const)}
            empty="Not chosen"
          />
        </FieldBox>
        <FieldBox label="Total Premium (annual)" htmlFor="np-prem" error={f.errors.totalPremium}>
          <input
            id="np-prem"
            inputMode="decimal"
            placeholder="1,200.00"
            {...f.text("totalPremium", "mono")}
          />
        </FieldBox>
        <FieldBox label="Total Coverage" htmlFor="np-cover" error={f.errors.totalCoverage}>
          <input
            id="np-cover"
            inputMode="decimal"
            placeholder="15,000"
            {...f.text("totalCoverage", "mono")}
          />
        </FieldBox>
        <FieldBox label="Policy Effective Date" htmlFor="np-eff" error={f.errors.effectiveDate}>
          <input id="np-eff" placeholder="MM-DD-YYYY" {...f.text("effectiveDate", "mono")} />
        </FieldBox>
        <FieldBox
          label="DOB (if not the customer's on file)"
          htmlFor="np-dob"
          error={f.errors.insuredDob}
        >
          <input id="np-dob" placeholder="MM-DD-YYYY" {...f.text("insuredDob", "mono")} />
        </FieldBox>
        <FieldBox label="Call Type" htmlFor="np-calltype">
          <Select
            id="np-calltype"
            value={v.callType}
            onChange={f.set("callType")}
            options={CALL_TYPES}
            empty="None"
          />
        </FieldBox>
        {can("records:reassign") && (
          <FieldBox label="Owner (the agent credited)" htmlFor="np-owner" error={f.errors.ownerId}>
            <Select
              id="np-owner"
              value={v.ownerId}
              onChange={(x) => f.set("ownerId")(x || me.user.id)}
              options={(users.data ?? []).map(
                (u) => [u.id, u.id === me.user.id ? `${u.fullName} (me)` : u.fullName] as const,
              )}
            />
          </FieldBox>
        )}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
